import { useState, useMemo } from 'react';

interface LineUser {
  line_display_name?: string;
  custom_display_name?: string;
  last_message_content?: string;
  line_user_id: string;
  is_pinned?: boolean;
  linked_reservations?: any[];
  reservation_ids?: (string | number)[];
  reservation_statuses?: string[];
  instance_res_data?: {
    start_date?: string;
    end_date?: string;
    pickup_date?: string;
    return_date?: string;
  };
  tags?: { id: string | number }[];
  [key: string]: any;
}

interface CustomDateFilter {
  date: string;
  type: 'pickup' | 'return';
}

export const useLineFilters = (users: LineUser[], initialFilters: string[] = [], tagFilters: (string | number)[] = []) => {
    const [filters, setFilters] = useState<string[]>(initialFilters);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [customDateFilter, setCustomDateFilter] = useState<CustomDateFilter | null>(null);

    const toggleFilter = (filterId: string) => {
        if (filterId === 'all') {
            setFilters([]);
            setSearchQuery('');
            setCustomDateFilter(null);
            return;
        }
        setFilters(prev =>
            prev.includes(filterId)
                ? prev.filter(f => f !== filterId)
                : [...prev, filterId]
        );
    };

    const filteredUsers = useMemo(() => {
        let result = users;

        // 1. Search Query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(u =>
                (u.line_display_name && u.line_display_name.toLowerCase().includes(query)) ||
                (u.custom_display_name && u.custom_display_name.toLowerCase().includes(query)) ||
                (u.last_message_content && u.last_message_content.toLowerCase().includes(query)) ||
                u.line_user_id.toLowerCase().includes(query)
            );
        }

        // 2. Custom Date Filter
        if (customDateFilter) {
            const { date, type } = customDateFilter;
            result = result.filter(u => {
                const res = u.instance_res_data;
                const targetDate = type === 'pickup' ? (res?.start_date || res?.pickup_date) : (res?.end_date || res?.return_date);
                return targetDate && targetDate.startsWith(date);
            });
        }

        // 3. Status Filters (Chip-based)
        if (filters.length > 0) {
            result = result.filter(u => {
                const match = filters.some(f => {
                    if (f === 'pinned') return u.is_pinned;
                    if (f === 'unlinked') return !u.linked_reservations || u.linked_reservations.length === 0;
                    if (f === 'today_pickup') return u.reservation_statuses?.includes('本日貸出');
                    if (f === 'today_return') return u.reservation_statuses?.includes('本日返却');
                    if (f === 'tomorrow_pickup') return u.reservation_statuses?.includes('明日貸出');
                    if (f === 'tomorrow_return') return u.reservation_statuses?.includes('明日返却');
                    if (f === 'in_use') {
                        return u.reservation_statuses?.includes('利用中');
                    }
                    return false;
                });
                return match;
            });
        }

        // 4. Tag Filters
        if (tagFilters.length > 0) {
            result = result.filter(u => {
                const userTagIds = (u.tags || []).map(t => t.id);
                return tagFilters.some(tid => userTagIds.includes(tid));
            });
        }

        return result;
    }, [users, filters, searchQuery, customDateFilter, tagFilters]);

    return {
        filters,
        toggleFilter,
        searchQuery,
        setSearchQuery,
        customDateFilter,
        setCustomDateFilter,
        filteredUsers
    };
};
